import {
  Button,
  DropdownInput,
  DropdownOption,
  JoonUIColor,
  Modal,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import SurveyStep from "./SurveyStep"
import LeavesLoader from "../../../components/leavesLoader/LeavesLoader"
import { pauseOptions, QUERY_KEYS } from "../../../constants"
import { cancelCoachSubscription } from "../../../networking/coaching/api"
import { cancelJoonitySubscription } from "../../../networking/families/api"
import { useFamilyQuery } from "../../../networking/queries"
import {
  CancelActionContent,
  CancelFlowModalType,
  CancelFlowStep,
  CancelFlowStepType,
} from "../../../types/cancelFlow"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { getSquelcheeImageUrl } from "../../../util/cancelFlow"
import { queryClient } from "../../../util/queryClient"
import useSettingsSubscriptionModalStore from "../hooks/useSettingsSubscriptionModalStore"

interface CancelModalStoreProps {
  isOpen: boolean
  onOpen: ({
    steps,
    subscriptionType,
  }: {
    steps: CancelFlowStep[]
    subscriptionType: CancelFlowModalType
  }) => void
  onClose: () => void
  steps: CancelFlowStep[]
  stepIndex: number
  setStepIndex: (stepIndex: number) => void
  subscriptionType: CancelFlowModalType

  // Survey step
  selectedAnswerIndex: number | null
  setSelectedAnswerIndex: (selectedAnswerIndex: number | null) => void
  freeformAnswer: string
  setFreeformAnswer: (freeformAnswer: string) => void
}

export const useCancelModalStore = create<CancelModalStoreProps>((set) => ({
  isOpen: false,
  onOpen: ({ steps, subscriptionType }) => {
    trackAnalyticEvent(ANALYTIC_EVENTS.INITIATE_CANCEL_SUB, {
      product: subscriptionType,
    })
    set({ isOpen: true, steps, subscriptionType })
  },
  onClose: () =>
    set({
      isOpen: false,
      steps: [],
      stepIndex: 0,
      selectedAnswerIndex: null,
      freeformAnswer: "",
      subscriptionType: CancelFlowModalType.JOONITY,
    }),
  steps: [],
  stepIndex: 0,
  setStepIndex: (stepIndex: number) => set({ stepIndex }),
  subscriptionType: CancelFlowModalType.JOONITY,
  selectedAnswerIndex: null,
  setSelectedAnswerIndex: (selectedAnswerIndex: number | null) =>
    set({ selectedAnswerIndex }),
  freeformAnswer: "",
  setFreeformAnswer: (freeformAnswer: string) => set({ freeformAnswer }),
}))

const CancelModal = () => {
  const [isCancelling, setIsCancelling] = useState(false)
  const [isPausing, setIsPausing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isExtending, setIsExtending] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false) // for all flows that are not cancelling
  const [selectedPauseOption, setSelectedPauseOption] =
    useState<DropdownOption>(pauseOptions[0])
  const [cancelActionContent, setCancelActionContent] =
    useState<CancelActionContent | null>(null)
  const { isOpen, onClose, steps, stepIndex, subscriptionType } =
    useCancelModalStore()
  const { onClose: onCloseSettingsSubscriptionModal } =
    useSettingsSubscriptionModalStore()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { data: family } = useFamilyQuery()
  const { t } = useTranslation()

  const localizedPauseOptions = pauseOptions.map((o) => ({
    ...o,
    label:
      o.value === 1
        ? t("CANCEL_PAUSE_MONTH_ONE")
        : t("CANCEL_PAUSE_MONTH_OTHER", { count: o.value }),
  }))

  const currentStep = steps[stepIndex]

  const onExtendTrial = async () => {
    // will always be joonity
    if (!family?.id) return
    setIsExtending(true)
    const response = await cancelJoonitySubscription({
      flowStepType: CancelFlowStepType.EXTEND_TRIAL,
      familyId: family.id,
    })
    setCancelActionContent(response)
    setIsExtending(false)
    setIsSuccess(true)
    trackAnalyticEvent(ANALYTIC_EVENTS.EXTEND_JOONITY_TRIAL)
    setTimeout(() => {
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      queryClient.invalidateQueries([QUERY_KEYS.SUBSCRIBER_INFO])
    }, 2000)
  }

  const onPause = async () => {
    // will always be coaching
    const pauseLength = Number(selectedPauseOption.value)
    if (Number.isNaN(pauseLength) || ![1, 2, 3].includes(pauseLength)) {
      return
    }
    setIsPausing(true)
    const response = await cancelCoachSubscription({
      flowStepType: CancelFlowStepType.PAUSE_SUBSCRIPTION,
      pauseLength,
    })
    setCancelActionContent(response)
    setIsPausing(false)
    setIsSuccess(true)
    trackAnalyticEvent(ANALYTIC_EVENTS.PAUSE_COACHING_SUBSCRIPTION, {
      length: pauseLength,
    })
    setTimeout(() => {
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      queryClient.invalidateQueries([QUERY_KEYS.COACH_SUBSCRIPTION])
    }, 2000)
  }

  const onAcceptOffer = async () => {
    setIsUpdating(true)
    let response: CancelActionContent | null = null
    if (subscriptionType === CancelFlowModalType.JOONITY) {
      if (!family?.id) {
        setIsUpdating(false)
        return
      }
      response = await cancelJoonitySubscription({
        flowStepType: CancelFlowStepType.SPECIAL_OFFER,
        familyId: family.id,
      })
    } else {
      response = await cancelCoachSubscription({
        flowStepType: CancelFlowStepType.SPECIAL_OFFER,
      })
    }
    setCancelActionContent(response)
    setIsUpdating(false)
    setIsSuccess(true)
    trackAnalyticEvent(ANALYTIC_EVENTS.ACCEPT_CANCEL_FLOW_SPECIAL_OFFER, {
      product: subscriptionType,
    })
    setTimeout(() => {
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      queryClient.invalidateQueries([QUERY_KEYS.SUBSCRIBER_INFO])
      queryClient.invalidateQueries([QUERY_KEYS.COACH_SUBSCRIPTION])
    }, 2000)
  }

  const onCancel = async () => {
    setIsCancelling(true)
    let response: CancelActionContent | null = null
    if (subscriptionType === CancelFlowModalType.JOONITY) {
      if (!family?.id) {
        setIsCancelling(false)
        return
      }
      response = await cancelJoonitySubscription({
        flowStepType: CancelFlowStepType.CANCEL,
        familyId: family.id,
      })
    } else {
      response = await cancelCoachSubscription({
        flowStepType: CancelFlowStepType.CANCEL,
      })
    }
    setCancelActionContent(response)
    setIsCancelling(false)
    trackAnalyticEvent(ANALYTIC_EVENTS.CANCEL_SUBSCRIPTION, {
      product: subscriptionType,
    })
    setTimeout(() => {
      queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
      queryClient.invalidateQueries([QUERY_KEYS.SUBSCRIBER_INFO])
      queryClient.invalidateQueries([QUERY_KEYS.COACH_SUBSCRIPTION])
    }, 2000)
  }

  const title = useMemo(() => {
    if (cancelActionContent) return cancelActionContent.title

    if (isCancelling) return t("CANCEL_CANCELING")
    if (isPausing) return t("CANCEL_PAUSING")
    if (isUpdating) return t("CANCEL_UPDATING")
    if (isExtending) return t("CANCEL_EXTENDING")

    if (!currentStep) return ""

    switch (currentStep.type) {
      case CancelFlowStepType.SPECIAL_OFFER:
        return currentStep.special_offer_header
      case CancelFlowStepType.CANCEL:
        return t("VQ_ARE_YOU_SURE")
      case CancelFlowStepType.EXTEND_TRIAL:
        return t("CANCEL_NEED_MORE_TIME")
      case CancelFlowStepType.PAUSE_SUBSCRIPTION:
        return t("CANCEL_NO_LONGER_INTERESTED")
      case CancelFlowStepType.SURVEY:
      default:
        return ""
    }
  }, [
    currentStep,
    isCancelling,
    isExtending,
    isPausing,
    isUpdating,
    cancelActionContent,
    t,
  ])

  const subtitle = useMemo(() => {
    if (cancelActionContent) return cancelActionContent.body

    if (isCancelling || isPausing || isUpdating || isExtending)
      return t("CANCEL_JUST_A_SECOND")

    if (!currentStep) return ""

    switch (currentStep.type) {
      case CancelFlowStepType.SPECIAL_OFFER:
        return currentStep.special_offer_body
      case CancelFlowStepType.CANCEL:
        return t("CANCEL_CANCEL_SUBTITLE")
      case CancelFlowStepType.EXTEND_TRIAL:
        return t("CANCEL_EXTEND_SUBTITLE")
      case CancelFlowStepType.PAUSE_SUBSCRIPTION:
        return t("CANCEL_PAUSE_SUBTITLE")
      case CancelFlowStepType.SURVEY:
      default:
        return ""
    }
  }, [
    currentStep,
    isCancelling,
    isExtending,
    isPausing,
    isUpdating,
    cancelActionContent,
    t,
  ])

  const showButtonSection = useMemo(() => {
    if (
      !currentStep ||
      isUpdating ||
      isCancelling ||
      isPausing ||
      isExtending ||
      isSuccess
    ) {
      return false
    }
    return currentStep.type !== CancelFlowStepType.CANCEL
  }, [currentStep, isCancelling, isExtending, isPausing, isSuccess, isUpdating])

  const shouldCenter =
    currentStep?.type === CancelFlowStepType.CANCEL ||
    isCancelling ||
    isPausing ||
    isUpdating ||
    isExtending ||
    !!cancelActionContent

  const shouldShowLoader =
    isPausing || isUpdating || isExtending || isCancelling

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0)
  }, [stepIndex])

  return (
    <Modal isOpen={isOpen} onClose={onClose} animate displayCloseIcon>
      <div
        className="flex flex-col max-w-[375px] w-full overflow-y-auto"
        ref={scrollRef}
      >
        {currentStep && (
          <>
            <img
              src={getSquelcheeImageUrl({
                step: currentStep,
                isLoading: isPausing || isUpdating || isExtending,
                isCancelling,
                isSuccess,
                isCancellingSuccess: !!cancelActionContent,
              })}
              className="w-full h-auto object-contain"
              alt="squelchee"
            />
            <div
              className={`w-full flex flex-col p-6 gap-4 border-t border-borderPrimary ${
                shouldCenter
                  ? "items-center text-center"
                  : "items-start text-left"
              }`}
            >
              {currentStep.type === CancelFlowStepType.SURVEY ? (
                <SurveyStep scrollRef={scrollRef} />
              ) : (
                <>
                  <Typography variant="h3">{title}</Typography>
                  <Typography variant="bodySmall">{subtitle}</Typography>
                  {shouldShowLoader ? (
                    <div className="my-2">
                      <LeavesLoader />
                    </div>
                  ) : (
                    currentStep.type !== CancelFlowStepType.CANCEL &&
                    !cancelActionContent && (
                      <div className="flex flex-col gap-2 bg-backgroundBlue p-3 rounded-lg shadow-md w-full">
                        {currentStep.type ===
                        CancelFlowStepType.EXTEND_TRIAL ? (
                          <>
                            <Typography variant="bodySmall">
                              {t("CANCEL_TRIAL_ENDS_ON", {
                                date: dayjs(
                                  currentStep.trial_end_current
                                ).format("MMMM D, YYYY"),
                              })}
                            </Typography>
                            <Button
                              buttonType="primary"
                              text={t("CANCEL_EXTEND_BTN")}
                              onClick={onExtendTrial}
                              fullWidth
                            />
                          </>
                        ) : currentStep.type ===
                          CancelFlowStepType.PAUSE_SUBSCRIPTION ? (
                          <>
                            <DropdownInput
                              label={
                                localizedPauseOptions.find(
                                  (o) => o.value === selectedPauseOption.value
                                )?.label
                              }
                              options={localizedPauseOptions}
                              onSelect={setSelectedPauseOption}
                              style={{
                                backgroundColor:
                                  JoonUIColor.background.primaryNeutral,
                              }}
                              menuStyle={{ zIndex: 100000 }}
                              fullWidth
                            />
                            <Button
                              buttonType="primary"
                              text={t("CANCEL_PAUSE_BTN")}
                              onClick={onPause}
                              fullWidth
                            />
                            <Typography variant="bodySmall">
                              {t("CANCEL_RESUME_ON", {
                                date: dayjs()
                                  .add(
                                    Number(selectedPauseOption.value),
                                    "month"
                                  )
                                  .format("MMM D, YYYY"),
                              })}
                            </Typography>
                          </>
                        ) : (
                          currentStep.special_offer_card && (
                            <>
                              <Typography variant="bodyBold">
                                {currentStep.special_offer_card?.header}
                              </Typography>
                              <Typography variant="h2">
                                {currentStep.special_offer_card?.body}{" "}
                                <Typography variant="body">
                                  {currentStep.special_offer_card?.body_subtext}
                                </Typography>
                              </Typography>
                              <Button
                                text={t("CANCEL_ACCEPT_OFFER")}
                                onClick={onAcceptOffer}
                                fullWidth
                              />
                            </>
                          )
                        )}
                      </div>
                    )
                  )}
                </>
              )}
              {currentStep.type === CancelFlowStepType.CANCEL &&
                !cancelActionContent &&
                !isCancelling && (
                  <div className="flex flex-row items-center justify-between gap-2 w-full">
                    <Button
                      buttonType="secondary"
                      text={t("CANCEL_NEVERMIND")}
                      onClick={onClose}
                    />
                    <Button text={t("CANCEL_YES_SURE")} onClick={onCancel} />
                  </div>
                )}
            </div>
            {cancelActionContent || isSuccess ? (
              <div className="w-full px-6 pb-4">
                <Button
                  buttonType="primary"
                  text={t("CANCEL_RETURN_HOME")}
                  fullWidth
                  onClick={() => {
                    onClose()
                    onCloseSettingsSubscriptionModal()
                  }}
                />
              </div>
            ) : (
              showButtonSection && <ButtonSection />
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

// will not show up if step === CancelFlowStepType.CANCEL
const ButtonSection = () => {
  const {
    stepIndex,
    setStepIndex,
    steps,
    selectedAnswerIndex,
    freeformAnswer,
    subscriptionType,
  } = useCancelModalStore()
  const { t } = useTranslation()

  const isSurvey = steps[stepIndex].type === CancelFlowStepType.SURVEY

  const BackButton = () => {
    if (stepIndex > 0) {
      return (
        <Button
          buttonType="secondary"
          size={isSurvey ? "medium" : "small"}
          text={t("QUEST_BACK")}
          onClick={() => setStepIndex(stepIndex - 1)}
        />
      )
    }
    return <div />
  }

  const NextButton = () => {
    if (stepIndex < steps.length - 1) {
      return (
        <Button
          buttonType={isSurvey ? "primary" : "secondary"}
          size={isSurvey ? "medium" : "small"}
          text={isSurvey ? t("FTUX_NEXT") : t("CANCEL_DECLINE_OFFER")}
          onClick={() => {
            if (isSurvey && selectedAnswerIndex !== null) {
              trackAnalyticEvent(ANALYTIC_EVENTS.WHEN_USER_SUBMITS_SURVEY, {
                answer:
                  steps[stepIndex].survey_answers?.[selectedAnswerIndex]
                    ?.analytic_param ?? "",
                reason: freeformAnswer,
              })
            }
            if (!isSurvey) {
              trackAnalyticEvent(ANALYTIC_EVENTS.DECLINE_CANCEL_OFFER, {
                product: subscriptionType,
                step: steps[stepIndex].type,
              })
            }
            setStepIndex(stepIndex + 1)
          }}
          disabled={
            isSurvey && (selectedAnswerIndex === null || !freeformAnswer)
          }
        />
      )
    }
    return <div />
  }

  return (
    <div className="flex flex-row px-6 py-4 items-center justify-between gap-2 border-t border-borderPrimary">
      <BackButton />
      <NextButton />
    </div>
  )
}

export default CancelModal
